import React, { useState, useEffect, memo, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';

import {
	EMBEDDED_CONFLUENCE_METRICS_PREFIX,
	EMBEDDED_EDITOR_TTI,
	useEmbeddedConfluenceEventMarkers,
} from '@atlassian/embedded-confluence';
import { getIframeObservable_DO_NOT_USE } from '@atlassian/embedded-confluence/getIframeObservable';

import { useIsTransition } from '@confluence/route-manager/entry-points/RouteState.ts';
import { getMark } from '@confluence/performance/entry-points/performance.ts';
import {
	EDIT_INTENT_EDIT_FMP_ACTION,
	EDIT_ROUTE_CHANGE_TO_FMP_ACTION,
	EDIT_INTENT_EDIT_TTI_ACTION,
	EDIT_ROUTE_CHANGE_TO_TTI_ACTION,
	EMBEDDED_EDIT_FMP_ACTION,
	EMBEDDED_EDIT_TTI_ACTION,
} from '@confluence/browser-metrics/entry-points/metric-keys.ts';
import { PageLoadMarkFMP } from '@confluence/browser-metrics/entry-points/PageLoadMarkFMP.ts';
import { UFOTransitionMarkFMP } from '@confluence/browser-metrics/entry-points/UFOTransitionMarkFMP.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { PageLoadEnd } from '@confluence/browser-metrics/entry-points/PageLoadEnd.ts';
import { CollabEditProviderMeasures } from '@confluence/action-measures/entry-points/CollabEditProviderMeasures.ts';
import { ExperienceActionMeasures } from '@confluence/action-measures/entry-points/ExperienceActionMeasures.ts';
import { COLLAB_EDIT_PROVIDER_MEASURES } from '@confluence/fabric-providers/entry-points/CollabEditProviderMeasures.ts';
import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { retrieveContentExtensionMetrics } from '@confluence/view-content-perf-metrics/entry-points/extensionAnalyticsHelpers.ts';
import { useConnectAppsData } from '@confluence/web-panel-location/entry-points/useConnectAppsData.ts';
import {
	ATL_GENERAL,
	ATL_FOOTER,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';

import { sendEditorStartedPageEvent } from '../utils/editorStartedAnalytics.ts';
import { EDIT_PAGE_LOAD, EMBEDDED_EDIT_PAGE_LOAD, EDITOR_METRIC } from '../perf.config.ts';
import { getBrowserExtensions } from '../utils/browserExtensions.ts';
import { getContentStats } from '../utils/getContentStats.ts';

export type ProseMirrorRenderedDataType = {
	proseMirrorRenderedFMP?: number;
	proseMirrorRenderedTimestamp?: number;
	startTime?: number;
	duration?: number;
	nodes?: object;
	nodeSize?: number;
	totalNodes?: number;
	topLevelNodeCount?: number;
	nodesInViewport?: Record<string, number>;
	distortedDuration?: boolean;
};

type PageLoadWrapperPropsType = {
	contentId: string;
	proseMirrorRenderedData: ProseMirrorRenderedDataType | null;
	disabled?: boolean;
	embeddedConfluenceSource?: string;
	isCreate?: any;
	extractedCollabEditProvider?: any;
	editSource?: string;
	isUpdatedNcsFMPMark?: boolean;
	isLivePage?: boolean;
	customPerfData?: Record<string, string | boolean | object>;
	draftBody?: string | null;
};

const markFMPExperienceActionMeasures = (fmpTimestamp?: number) => {
	ExperienceActionMeasures.markMeasureEnd(EDIT_INTENT_EDIT_FMP_ACTION, fmpTimestamp);
	ExperienceActionMeasures.markMeasureEnd(EDIT_ROUTE_CHANGE_TO_FMP_ACTION, fmpTimestamp);
	ExperienceActionMeasures.markMeasureEnd(EMBEDDED_EDIT_FMP_ACTION, fmpTimestamp);
};

const markTTIExperienceActionMeasures = (ttiTimestamp?: number) => {
	ExperienceActionMeasures.markMeasureEnd(EDIT_INTENT_EDIT_TTI_ACTION, ttiTimestamp);
	ExperienceActionMeasures.markMeasureEnd(EDIT_ROUTE_CHANGE_TO_TTI_ACTION, ttiTimestamp);
	CollabEditProviderMeasures.markMeasureEnd(
		COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_INIT,
		ttiTimestamp,
	);
	ExperienceActionMeasures.markMeasureEnd(EMBEDDED_EDIT_TTI_ACTION, ttiTimestamp);
};

const PageLoadWrapperComponent = memo(
	({
		contentId,
		proseMirrorRenderedData,
		disabled,
		isCreate,
		editSource,
		embeddedConfluenceSource,
		isLivePage,
		customPerfData,
		draftBody,
	}: PageLoadWrapperPropsType) => {
		// When editor content is rendered on server, FMP should be reported as TTR on initial load,
		// as the moment when initial HTML is rendered, not when proseMirrorRenderedData is available.
		const isTransition = useIsTransition();
		const initialFmpTimestamp =
			!isTransition && window.__SSR_RENDERED__ ? getMark('CFP-63.ssr-ttr') : undefined;

		const { createAnalyticsEvent } = useAnalyticsEvents();
		const [renderPageLoadEnd, setRenderPageLoadEnd] = useState<boolean>(false);
		const [renderPageSegmentLoadEnd, setPageSegmentLoadEnd] = useState<boolean>(false);
		const [editorEnabledTimestamp, setEditorEnabledTimestamp] = useState<number | undefined>();
		const [fmpTimestamp, setFMPTimestamp] = useState<number | undefined>();
		const [{ hasInitialDraft }] = useNativeCollabState();

		const sendMetrics = useEmbeddedConfluenceEventMarkers();

		const panelsModuleKeysAtlGeneral = useConnectAppsData({
			contentId,
			location: ATL_GENERAL,
		});
		const panelsModuleKeysAtlFooter = useConnectAppsData({
			contentId,
			location: ATL_FOOTER,
		});

		const contentExtensionMetrics = useMemo(() => {
			let metrics = customPerfData?.contentExtensionMetrics || {};
			if (draftBody && Object.keys(metrics).length === 0) {
				metrics = retrieveContentExtensionMetrics(draftBody);
			}
			return metrics;
		}, [draftBody, customPerfData?.contentExtensionMetrics]);

		const smartlinksInitialState =
			!isTransition && window.__SSR_RENDERED__ && window['__SSR_SMARTLINKS__'];
		const preloadSmartlinksCount = useMemo(() => {
			if (!smartlinksInitialState) {
				return 0;
			}
			return Object.keys(smartlinksInitialState).length;
		}, [smartlinksInitialState]);

		// When buffering is enabled, there are scenarios in which TTI can be marked prior to FMP.
		// In these cases, we wait until PM data becomes available and set TTI to be FMP.
		const markPageLoad = (
			fmpTimestamp: number,
			stopTime: number,
			isLivePage: boolean | undefined,
		) => {
			const ttiTimestamp = stopTime < fmpTimestamp ? fmpTimestamp : stopTime;
			markTTIExperienceActionMeasures(ttiTimestamp);
			setEditorEnabledTimestamp(ttiTimestamp);
			setPageSegmentLoadEnd(true);
			if (!isLivePage) {
				setRenderPageLoadEnd(true);
			}
		};

		const pmRenderedTimestamp = proseMirrorRenderedData?.proseMirrorRenderedTimestamp;

		const documentStats = useMemo(() => getContentStats(draftBody), [draftBody]);

		const totalNodes = useMemo(() => {
			const allNodesCount: number[] = Object.values(documentStats);
			return allNodesCount.reduce((acc: number, nodeCnt: number) => acc + nodeCnt, 0);
		}, [documentStats]);

		useEffect(() => {
			const renderContentTimestamp = pmRenderedTimestamp || initialFmpTimestamp;

			// mark FMP only once proseMirrorRenderedData has a defined value
			if (!fmpTimestamp && renderContentTimestamp) {
				setFMPTimestamp(renderContentTimestamp);
				markFMPExperienceActionMeasures(renderContentTimestamp);
			}
			// Initially, when TTI is being marked for the first time, PM data either be defined or undefined
			if (typeof editorEnabledTimestamp === 'undefined' && !disabled) {
				const stopTime = performance.now();
				if (editSource === 'EmbeddedEditor') {
					const observableObject = getIframeObservable_DO_NOT_USE();
					const uniqueKey = observableObject?.object?.uniqueKey;
					sendMetrics(
						{
							timestamp: Date.now(),
							...(uniqueKey ? { customData: { uniqueKey } } : {}),
						},
						`${EMBEDDED_CONFLUENCE_METRICS_PREFIX}${EMBEDDED_EDITOR_TTI}`,
					);
				}
				// if PM data is already available, mark the TTI and page load end
				if (fmpTimestamp && !renderPageLoadEnd) {
					markPageLoad(fmpTimestamp, stopTime, isLivePage);
				} else {
					// if PM data is not yet available, save the TTI to be used later when FMP becomes defined
					markTTIExperienceActionMeasures(stopTime);
					setEditorEnabledTimestamp(stopTime);
				}
				sendEditorStartedPageEvent(createAnalyticsEvent, {
					isCreate,
				});
			} else if (editorEnabledTimestamp) {
				// once PM data is available, mark the TTI and page load end
				if (fmpTimestamp && !renderPageLoadEnd) {
					markPageLoad(fmpTimestamp, editorEnabledTimestamp, isLivePage);
				}
			}
		}, [
			initialFmpTimestamp,
			pmRenderedTimestamp,
			editorEnabledTimestamp,
			fmpTimestamp,
			disabled,
			renderPageLoadEnd,
			createAnalyticsEvent,
			isCreate,
			isLivePage,
			customPerfData,
			editSource,
			sendMetrics,
		]);

		// PageSegmentLoadEnd & PageLoadEnd have common custom data. PageLoadEnd is only fired for non-live pages.

		const commonCustomData = {
			fabric: true,
			isLivePage,
			embeddedConfluenceSource,
			documentStats,
			contentExtensionMetrics,
			preloadSmartlinksCount,
			totalNodes,
		};

		return (
			<>
				<UFOLoadHold name="AwaitEditorSegment" hold={!renderPageSegmentLoadEnd} />
				<PageSegmentLoadStart metric={EDITOR_METRIC} />
				{fmpTimestamp && (
					<>
						<PageLoadMarkFMP timestamp={fmpTimestamp} />
						<UFOTransitionMarkFMP timestamp={fmpTimestamp} />
					</>
				)}
				{renderPageSegmentLoadEnd && (
					<PageSegmentLoadEnd
						key={`end-segment-${contentId}`}
						metric={EDITOR_METRIC}
						stopTime={editorEnabledTimestamp}
						customData={{
							...commonCustomData,
							...customPerfData,
						}}
					/>
				)}
				{renderPageLoadEnd && (
					<PageLoadEnd
						key={`end-page-${contentId}`}
						metric={editSource === 'EmbeddedEditor' ? EMBEDDED_EDIT_PAGE_LOAD : EDIT_PAGE_LOAD}
						stopTime={editorEnabledTimestamp}
						customData={{
							...commonCustomData,
							contentId,
							isCreate,
							collabService: 'ncs',
							experienceActionMeasures: ExperienceActionMeasures.getMeasures(),
							collabEditProviderMeasures: CollabEditProviderMeasures.getMeasures(),
							editSource,
							proseMirrorRenderedData,
							hasInitialDraft,
							browserExtensions: getBrowserExtensions(),
							webPanelsAtlGeneral: panelsModuleKeysAtlGeneral,
							webPanelsAtlFooter: panelsModuleKeysAtlFooter,
						}}
					/>
				)}
			</>
		);
	},
);

export const PageLoadWrapper: React.FC<PageLoadWrapperPropsType> = withTransparentErrorBoundary({
	attribution: Attribution.BACKBONE,
})(PageLoadWrapperComponent);
